import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { get as _get, has as _has } from 'lodash';
import { filter, Observable, Subject, takeUntil } from 'rxjs';

import { AnalyticsActions, AnalyticsFacade, AnalyticsSelectors } from '@amfam/shared/analytics';
import { BillingAnalytics, BillingPaymentPaths } from '@amfam/shared/models';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { ConfirmationModel } from '@amfam/ui-kit';

@Component({
  selector: 'ds-bill-account-add-confirmation',
  templateUrl: './bill-account-add-confirmation.component.html',
  styleUrls: ['../billing-payment-confirmation/billing-payment-confirmation.component.scss'],
  standalone: false
})
export class BillAccountAddConfirmationComponent implements OnInit, OnDestroy {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() public confirmationObj: any;
  @Input() public isMyAccountAdmin: boolean;

  public cssClasses = 'material noshadow expandable payment-automatic';

  public columns = [{}];
  public rows = [];

  confirmationData: ConfirmationModel;

  // Make the route constants available on the template
  billingPaths = BillingPaymentPaths;

  private analyticsLoaded$: Observable<boolean>;
  private billAccountNumber: string;
  private stop$ = new Subject<void>();

  constructor(
    private analyticsFacade: AnalyticsFacade,
    private copyService: CopyService,
    private store: Store
  ) {}

  ngOnInit() {
    this.buildConfirmationData();
    this.columns = this.confirmationObj.columnNamePropMap;
    this.rows = this.confirmationObj.rowsPropValueMap;

    this.billAccountNumber = _get(this.confirmationObj, 'billAccountNumber', '');

    if (_has(this.confirmationObj, 'subCategory')) {
      if (this.confirmationObj.subCategory === 'add') {
        if (!this.isMyAccountAdmin) {
          this.analyticsFacade.trackPage(BillingAnalytics.pageRegisteredBaComplete);
        } else {
          this.analyticsLoaded$ = this.store
            .select(AnalyticsSelectors.selectAdobeAnalyticsLoaded)
            .pipe(
              filter(data => data),
              takeUntil(this.stop$)
            );

          this.analyticsLoaded$.subscribe(() => {
            this.store.dispatch(
              AnalyticsActions.sendAnalytic({
                options: {
                  page: BillingAnalytics.pageRegisteredCompleteMyAccountAdmin
                }
              })
            );
          });
        }
      } else if (this.confirmationObj.subCategory === 'remove') {
        if (!this.isMyAccountAdmin) {
          this.analyticsFacade.trackPage(BillingAnalytics.pageUnRegisterBaComplete);
        } else {
          this.store.dispatch(
            AnalyticsActions.sendAnalytic({
              options: {
                page: BillingAnalytics.pageUnRegisterBaCompleteAdmin
              }
            })
          );
        }
      }
    }
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  buildConfirmationData() {
    let headerPath = '';
    let nextSteps = '';
    if (this.confirmationObj.subCategory === 'add') {
      headerPath = 'billing.addBillAccountHeader';
      nextSteps =
        this.copyService.getCopy('billing.nextStepsConfirmationMsg') +
        '\n\n' +
        this.copyService.getCopy('billing.billAcctAddConfText');
    } else if (this.confirmationObj.subCategory === 'remove') {
      headerPath = 'billing.stopManagingBillAccountHeader';
      nextSteps = this.copyService.getCopy('billing.stopManagingBillAccountNextSteps');
    }

    this.confirmationData = {
      confirmation: {
        heading: this.copyService.getCopy(headerPath, { billAct: this.confirmationObj.nickName })
      },
      details: [
        {
          heading: 'Details',
          tableColumns: this.confirmationObj.columnNamePropMap,
          tableRows: this.confirmationObj.rowsPropValueMap
        }
      ],
      nextSteps: {
        heading: 'Next Steps',
        text: nextSteps
      }
    };

    if (this.confirmationObj.subCategory === 'add') {
      this.confirmationData.ctaArea = {
        heading: 'Set up automatic payments',
        benefits: ['No late fees', 'Set it and forget it', 'Cancel anytime'],
        button: {
          title: this.copyService.getCopy('billing.setUpAutoPaymentBtn'),
          callback: this.setupAutoPayNavigation.bind(this)
        }
      };
    }
  }

  returnToBillingandPayments() {
    this.store.dispatch(
      fromRouterActions.Go({
        path: [BillingPaymentPaths.BILLING_SECTION_PAGE]
      })
    );
  }

  public setupAutoPayNavigation() {
    this.store.dispatch(
      fromRouterActions.Go({
        path: [BillingPaymentPaths.BILLING_SECTION_PAGE, this.billAccountNumber]
      })
    );
  }
}
